import { useState } from 'react';
import { Link } from 'react-router';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Mail, ArrowLeft, Loader2, CheckCircle, AlertTriangle } from 'lucide-react';
import api from '@/lib/api';

const EVA_LOGO_URL = "/eva-icon-square.png";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [devResetUrl, setDevResetUrl] = useState(null);
  const [emailSent, setEmailSent] = useState(true);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email.trim() || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/auth/forgot-password', { email });
      setEmailSent(res.data.email_sent !== false);
      setDevResetUrl(res.data.dev_reset_url || null);
      setSubmitted(true);
    } catch (_err) {
      // Still show success to prevent email enumeration
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-secondary flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-12 pb-12 text-center">
            {emailSent ? (
              <>
                <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-8 h-8 text-success" />
                </div>
                <h2 className="text-2xl font-bold text-foreground mb-3">Check your email</h2>
                <p className="text-muted-foreground mb-8">
                  If an account exists with <span className="font-medium">{email}</span>,
                  you&apos;ll receive a password reset link shortly.
                </p>
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Didn&apos;t receive the email? Check your spam folder or try again.
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => setSubmitted(false)}
                    className="mr-2"
                  >
                    Try again
                  </Button>
                  <Link to="/login">
                    <Button variant="ghost">
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back to login
                    </Button>
                  </Link>
                </div>
              </>
            ) : (
              <>
                <div className="w-16 h-16 bg-yellow-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <AlertTriangle className="w-8 h-8 text-yellow-500" />
                </div>
                <h2 className="text-2xl font-bold text-foreground mb-3">Email not configured</h2>
                <p className="text-muted-foreground mb-6">
                  Email delivery is not set up. A superadmin can enable it under
                  Integrations in the admin panel.
                </p>
                {devResetUrl && (
                  <div className="bg-muted border border-border rounded-lg p-4 mb-6 text-left">
                    <p className="text-xs font-semibold text-yellow-600 dark:text-yellow-400 uppercase tracking-wide mb-2">
                      Dev Mode — Reset Link
                    </p>
                    <a
                      href={devResetUrl}
                      className="text-sm text-primary hover:underline break-all"
                    >
                      {devResetUrl}
                    </a>
                  </div>
                )}
                <div className="space-y-4">
                  <Button
                    variant="outline"
                    onClick={() => { setSubmitted(false); setDevResetUrl(null); setEmailSent(true); }}
                    className="mr-2"
                  >
                    Try again
                  </Button>
                  <Link to="/login">
                    <Button variant="ghost">
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back to login
                    </Button>
                  </Link>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center pb-6">
          {/* Logo */}
          <img src={EVA_LOGO_URL} alt="Eva" className="w-12 h-12 rounded-xl mx-auto mb-5" />
          <CardTitle className="text-2xl font-bold">Forgot your password?</CardTitle>
          <CardDescription className="mt-1.5">
            Enter your email and we&apos;ll send you a reset link
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email" className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-muted-foreground" />
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="forgot-email"
                required
              />
            </div>

            {/* Submit Button */}
            <Button 
              type="submit" 
              className="w-full bg-primary hover:bg-primary/90"
              disabled={loading}
              data-testid="forgot-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Reset Link'
              )}
            </Button>

            {/* Back to Login */}
            <div className="text-center pt-2">
              <Link 
                to="/login" 
                className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to login
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
